import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { CheckCircle2, ArrowLeft, BadgeCheck, Zap } from "lucide-react";

import { useAuth } from "../auth/AuthProvider";
import { useViewSubscription } from "../../hooks/subscription/subscription.view";
import { getSubscriptionRecord, getPlanPricing } from "./subscriptionUtils";
import api from "../../services/api";
import "../../assets/styles/subscription.css";

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
const formatPrice = (pricing) => {
  if (!pricing) return null;
  const amount =
    pricing.offerPrice ?? pricing.salePrice ?? pricing.amount ?? pricing.price;
  const currency = "AED ";
  return amount != null ? `${currency}${amount}` : null;
};

const getOriginalPrice = (pricing) => {
  if (!pricing) return null;
  const offer    = pricing.offerPrice ?? pricing.salePrice ?? pricing.amount ?? pricing.price;
  const original = pricing.originalPrice ?? (pricing.offerPrice != null ? pricing.price : null);
  if (original != null && offer != null && Number(original) !== Number(offer)) {
    const currency = "AED ";
    return `${currency}${original}`;
  }
  return null;
};

/* ─────────────────────────────────────────
   PLAN CARD
───────────────────────────────────────── */
const PlanCardItem = ({ plan, isCurrent, onSelect, isSelecting }) => {
  const defaultPricing   = getPlanPricing(plan);
  const companyPricing   = getPlanPricing(plan, "company")     ?? defaultPricing;
  const individualPricing= getPlanPricing(plan, "individual")  ??
                           getPlanPricing(plan, "user")         ?? defaultPricing;

  const displayPrice   = formatPrice(companyPricing) || formatPrice(individualPricing);
  const originalPrice  = getOriginalPrice(companyPricing) || getOriginalPrice(individualPricing);
  const priceSuffix    = companyPricing ? "/ month" : "/ user";

  const features = Array.isArray(plan?.features) ? plan.features : [];
  const limits   = plan?.limits ?? {};

  return (
    <div className={`plan-card-custom${isCurrent ? " plan-card-custom--current" : ""}`}>
      {/* HEADER BAR */}
      <div className="plan-card-header-bar">
        {plan?.headerName || plan?.name || "Plan"}
        {isCurrent && <span className="plan-current-badge">✓ Current Plan</span>}
        {plan?.isPopular && !isCurrent && (
          <span style={{ background: "#fde68a", color: "#92400e", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
            POPULAR
          </span>
        )}
      </div>

      {/* BODY */}
      <div className="plan-card-body">
        {/* row: badges */}
        <div className="plan-card-row-top">
          <div className="plan-card-badges">
            <span className={`plan-badge-pill ${plan?.type === "paid" ? "pill-paid" : "pill-unpaid"}`}>
              {plan?.type === "paid" ? "Paid" : "Free"}
            </span>
            <span className={`plan-badge-pill ${plan?.isActive !== false ? "pill-active" : "pill-inactive"}`}>
              {plan?.isActive !== false ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* title */}
        <p className="plan-card-title">{plan?.name || "Unnamed Plan"}</p>

        {/* price */}
        {displayPrice && (
          <div className="plan-card-price-section">
            <span className="plan-card-offer-price">{displayPrice}</span>
            {originalPrice && (
              <span className="plan-card-original-price">{originalPrice}</span>
            )}
            <span className="plan-card-duration-slash">{priceSuffix}</span>
          </div>
        )}

        {/* description */}
        {plan?.description && (
          <p className="plan-card-description">{plan.description}</p>
        )}

        {/* features */}
        {features.length > 0 && (
          <ul className="plan-card-features-list">
            {features.map((f, i) => (
              <li key={i} className="plan-card-feature-item">
                <CheckCircle2 size={14} className="plan-card-feature-check" color="#5c308d" />
                {f}
              </li>
            ))}
          </ul>
        )}

        <hr className="plan-card-divider" />

        {/* limits */}
        <div className="plan-card-limits-section">
          {limits.maxUsers != null && (
            <div className="plan-card-limit-row">
              <span className="plan-card-limit-label">Max Users</span>
              <span className="plan-card-limit-value">{limits.maxUsers}</span>
            </div>
          )}
          {limits.maxProducts != null && (
            <div className="plan-card-limit-row">
              <span className="plan-card-limit-label">Max Products</span>
              <span className="plan-card-limit-value">{limits.maxProducts}</span>
            </div>
          )}
          {limits.storageSpaceInGB != null && (
            <div className="plan-card-limit-row">
              <span className="plan-card-limit-label">Storage</span>
              <span className="plan-card-limit-value">{limits.storageSpaceInGB} GB</span>
            </div>
          )}
          {limits.maxApplicationImages != null && (
            <div className="plan-card-limit-row">
              <span className="plan-card-limit-label">App Images</span>
              <span className="plan-card-limit-value">{limits.maxApplicationImages}</span>
            </div>
          )}
        </div>

        {/* CTA button */}
        <button
          className="plan-select-button"
          disabled={isCurrent || isSelecting}
          onClick={() => onSelect(plan)}
          style={isCurrent ? { background: "#f3f4f6", color: "#6b7280", borderColor: "#d1d5db", cursor: "not-allowed" } : {}}
        >
          {isCurrent
            ? "Current Plan"
            : isSelecting
            ? "Processing..."
            : "Select Plan"}
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────── */
const Plans = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, isError, refetch } = useViewSubscription();
  const subscription   = getSubscriptionRecord(data);
  const availablePlans = subscription?.availablePlans ?? [];
  const currentPlanId  =
    subscription?.plan?._id ||
    subscription?.plan?.id  ||
    subscription?.planId    ||
    subscription?.planCode  ||
    null;

  const [selectingId, setSelectingId] = useState(null);

  const handleSelect = async (plan) => {
    const id = plan._id || plan.id;
    if (!id) { toast.error("Invalid plan"); return; }
    const companyId = subscription?.companyId || user?.companyId;
    if (!companyId) {
      toast.error("Company ID is missing from your admin profile. Please sign in again.");
      return;
    }

    setSelectingId(id);
    try {
      await api.post("/auth/subscribe", { planId: id, companyId });
      toast.success(`Switched to ${plan.name || "new plan"} successfully`);
      await refetch();
      navigate("/subscription");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to select plan. Please try again.");
    } finally {
      setSelectingId(null);
    }
  };

  /* ── LOADING / ERROR ── */
  if (isLoading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
        Loading available plans...
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#dc2626" }}>
        Failed to load plans. Please try again.
      </div>
    );
  }

  /* ── RENDER ── */
  return (
    <div className="subscription-page" style={{ padding: "24px 0" }}>

      {/* BACK + HEADING */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "none", border: "none", cursor: "pointer",
            color: "#5c308d", fontWeight: 600, fontSize: 14, padding: 0,
          }}
        >
          <ArrowLeft size={16} /> Back
        </button>
      </div>

      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, background: "#5c308d", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Zap size={18} color="#fff" />
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1f2937" }}>
            Choose a Plan
          </h1>
        </div>
        <p style={{ margin: "6px 0 0 46px", fontSize: 13, color: "#6b7280" }}>
          {availablePlans.length > 0
            ? `${availablePlans.length} plan${availablePlans.length > 1 ? "s" : ""} available — select one to upgrade or switch`
            : "No plans available at the moment"}
        </p>
      </div>

      {/* CURRENT PLAN NOTICE */}
      {currentPlanId && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "12px 16px", borderRadius: 10,
          background: "#ede9fe", border: "1px solid #c4b5fd",
          marginBottom: 24,
        }}>
          <BadgeCheck size={18} color="#5c308d" />
          <p style={{ margin: 0, fontSize: 13, color: "#5c308d", fontWeight: 600 }}>
            You are on: <span style={{ fontWeight: 800 }}>{subscription?.plan?.name || "Active Plan"}</span>
          </p>
        </div>
      )}

      {/* PLANS GRID */}
      {availablePlans.length === 0 ? (
        <div style={{
          padding: "48px 24px", textAlign: "center",
          borderRadius: 12, border: "2px dashed #e5e7eb", background: "#f9fafb",
        }}>
          <BadgeCheck size={36} color="#d1d5db" style={{ marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 15, color: "#6b7280", fontWeight: 600 }}>
            No plans available right now
          </p>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#9ca3af" }}>
            Please contact support to get access to subscription plans.
          </p>
        </div>
      ) : (
        <div className="plans-grid">
          {availablePlans.map((plan) => {
            const id = plan._id || plan.id;
            return (
              <PlanCardItem
                key={id}
                plan={plan}
                isCurrent={currentPlanId === id}
                onSelect={handleSelect}
                isSelecting={selectingId === id}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Plans;
